import { Inmate } from '@reliance/types/models/Inmate';
import { TextMessage } from '@reliance/types/models/TextMessage';
import type { ColumnFiltersState, PaginationState, SortingState } from '@tanstack/react-table';
import type { OnChangeFn } from '@tanstack/react-table';
import moment from 'moment';
import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import Badge from 'react-bootstrap/Badge';
import { formatDate } from '../format/DateTime';
import { formatPhoneNumber } from '../format/PhoneNumber';
import InmateDetailBlock from '../inmate/InmateDetailBlock';
import BaseHtmlTable from '../table/BaseHtmlTable';
import { createColumnHelper } from '@tanstack/react-table';
import TextImage from './TextImage';

/** Column meta: map UI filter values to GET /text-message index query params (see TextMessage::index). */
export type TextMessageTableColumnMeta = {
  filterComponent?: string;
  // This will allow us to supply the query parameter that the API expects for this column.
  apiQueryParam?: string;
  // Map the user's input to the API's expected value (date ranges, etc)
  mapFilterToApi?: (value: unknown) => string;
};

type ColumnDefWithId = { id?: string; meta?: TextMessageTableColumnMeta };

const defaultDateRange = () => ({
  startDate: moment().subtract(1, 'days').startOf('day').format('YYYY-MM-DD HH:mm:ss'),
  endDate: moment().endOf('day').format('YYYY-MM-DD HH:mm:ss'),
});

export function mapFilters(
  raw: Record<string, unknown>,
  columnDefs: ColumnDefWithId[],
  dateDefaults: () => { startDate: string; endDate: string } = defaultDateRange
): Record<string, unknown> {
  const out: Record<string, unknown> = {
    page: raw.page,
    perPage: raw.perPage,
    orderBy: raw.orderBy,
  };
  const dr = raw.createdDate as { startDate?: string; endDate?: string } | undefined;
  if (dr?.startDate && dr?.endDate) {
    out.startDate = dr.startDate.replace(/\//g, '-') + ' 00:00:00';
    out.endDate = dr.endDate.replace(/\//g, '-') + ' 23:59:59';
  } else {
    const d = dateDefaults();
    out.startDate = d.startDate;
    out.endDate = d.endDate;
  }
  const singles: Record<string, string> = {};
  for (const col of columnDefs) {
    const id = col.id;
    const meta = col.meta;
    if (!id || !meta?.apiQueryParam) {
      continue;
    }
    const v = raw[id];
    if (v === undefined || v === '') {
      continue;
    }
    const mapped = meta.mapFilterToApi ? meta.mapFilterToApi(v) : String(v).trim();
    if (!mapped) {
      continue;
    }
    singles[meta.apiQueryParam] = mapped;
  }
  Object.assign(out, singles);
  return out;
}

const directionApiValue = (value: string): string => {
  const d = String(value ?? '').trim();
  if (d === 'In') {
    return 'Inbound';
  }
  if (d === 'Out') {
    return 'Outbound';
  }
  return d;
};

export const COLUMNS = [
  { id: 'createdDate', meta: { filterComponent: 'DateRangeFilter', sortName: 'createdDate' } },
  { id: 'facility', meta: { sortName: 'facility.name' } },
  { id: 'inmate', meta: { apiQueryParam: 'inmates', sortName: 'inmate' } },
  { id: 'customerAni', meta: { apiQueryParam: 'customers', sortName: 'customerAni' } },
  { id: 'customerName', meta: { apiQueryParam: 'customers', sortName: 'customerName' } },
  {
    id: 'direction',
    meta: {
      apiQueryParam: 'direction',
      sortName: 'direction',
      mapFilterToApi: directionApiValue,
    },
  },
  { id: 'status', meta: { apiQueryParam: 'status', sortName: 'status' } },
  { id: 'message', meta: { apiQueryParam: 'message', sortName: 'message' } },
];

type TableProps = {
  data: TextMessage[];
  loadImages?: boolean;
  loading?: boolean;
  paginationMode?: 'client' | 'server';
  page?: number;
  totalPages?: number;
  total?: number;
  perPage?: number;
  serverPagination?: PaginationState;
  onServerPaginationChange?: OnChangeFn<PaginationState>;
  serverSorting?: SortingState;
  onServerSortingChange?: OnChangeFn<SortingState>;
  serverColumnFilters?: ColumnFiltersState;
  onServerColumnFiltersChange?: OnChangeFn<ColumnFiltersState>;
  visibleColumns?: string[];
  hiddenColumns?: string[];
  initialColumnFilters?: { id: string; value: unknown }[];
  onShowProviderStatus?: (textMessage: TextMessage) => void;
};

const Table: React.FC<TableProps> = (props) => {
  const {
    data,
    loadImages,
    loading,
    paginationMode,
    page,
    totalPages,
    total,
    perPage,
    serverPagination,
    onServerPaginationChange,
    serverSorting,
    onServerSortingChange,
    serverColumnFilters,
    onServerColumnFiltersChange,
    visibleColumns,
    hiddenColumns,
    initialColumnFilters,
    onShowProviderStatus,
  } = props;

  const columnHelper = createColumnHelper<TextMessage>();

  // Define a superset of columns that can be shown in different contexts
  const allColumns = useMemo(() => [
    columnHelper.accessor((row) => {
      return formatDate(row.createdDate);
    }, {
      id: 'createdDate',
      header: 'Date',
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: 'dateRange',
      meta: {
        filterComponent: 'DateRangeFilter',
      },
    }),
    columnHelper.accessor((row) => {
      const facility = row.facility ?? row.inmate?.facility;
      return facility?.name ?? 'Unassigned';
    }, {
      id: 'facility',
      header: 'Facility',
      enableSorting: true,
      cell: (info) => {
        const row = info.row.original;
        const facility = row.facility ?? row.inmate?.facility;
        if (!facility) {
          return 'Unassigned';
        }
        return (
          <>
            {facility.name} ({facility.state})
          </>
        );
      },
    }),
    columnHelper.accessor((row) => row.inmate, {
      id: 'inmate',
      header: 'Inmate',
      enableSorting: true,
      enableColumnFilter: true,
      meta: {
        apiQueryParam: 'inmates',
      },
      cell: (info) => {
        const row = info.row.original;
        return <InmateDetailBlock inmate={row.inmate as Inmate} />;
      },
    }),
    columnHelper.accessor((row) => {
      return formatPhoneNumber(row.customerAni);
    }, {
      id: 'customerAni',
      header: 'Customer',
      enableSorting: true,
      enableColumnFilter: true,
      meta: {
        apiQueryParam: 'customers',
      },
      cell: (info) => {
        const row = info.row.original;
        return (
          <>
            <Link to={`/account/${encodeURIComponent(row.customerAni)}`}>{formatPhoneNumber(row.customerAni)}</Link>
            {row.customerFirstName && (
              <>
                <br />
                <small>{row.customerFirstName} {row.customerLastName}</small>
              </>
            )}
          </>
        );
      },
    }),
    columnHelper.accessor((row) => {
      return `${row.customerFirstName} ${row.customerLastName}`;
    }, {
      id: 'customerName',
      header: 'Customer Name',
      enableSorting: true,
      enableColumnFilter: true,
      meta: {
        apiQueryParam: 'customers',
      },
    }),
    columnHelper.accessor((row) => {
      return row.direction === 'Inbound' ? 'In' : 'Out';
    }, {
      id: 'direction',
      header: 'Direction',
      enableSorting: true,
      enableColumnFilter: true,
      meta: {
        apiQueryParam: 'direction',
        mapFilterToApi: directionApiValue,
      },
    }),
    columnHelper.accessor('status', {
      id: 'status',
      header: 'Status',
      enableSorting: true,
      enableColumnFilter: true,
      meta: {
        apiQueryParam: 'status',
      },
      cell: (info) => {
        const row = info.row.original;
        const statusText = String(info.getValue() ?? '');
        if (onShowProviderStatus) {
          return (
            <>
              <i
                className="fas fa-info-circle"
                style={{ cursor: 'pointer' }}
                role="button"
                tabIndex={0}
                onClick={() => onShowProviderStatus(row)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    onShowProviderStatus(row);
                  }
                }}
              />{' '}
              {statusText}
            </>
          );
        }
        return statusText;
      },
    }),
    columnHelper.accessor('message', {
      id: 'message',
      header: 'Contents',
      enableSorting: true,
      enableColumnFilter: true,
      meta: {
        apiQueryParam: 'message',
      },
      cell: (info) => {
        const row = info.row.original;
        return (
          <>
            {row.sentBy === 'System' && (
              <Badge bg="dark" className="me-2">AUTO</Badge>
            )}
            <span>{row.message}</span>
            {loadImages && row.attachmentUrl ? (
              <div className="mt-1">
                <TextImage textMessage={row} />
              </div>
            ) : null}
          </>
        );
      },
    }),
  ], [columnHelper, loadImages, onShowProviderStatus]);

  // Default columns when no visibility is specified
  const defaultVisibleColumns = useMemo(() => [
    'createdDate',
    'facility',
    'inmate',
    'customerAni',
    'direction',
    'status',
    'message',
  ], []);

  const columns = useMemo(() => {
    const targetVisible = (visibleColumns && visibleColumns.length > 0)
      ? visibleColumns
      : defaultVisibleColumns;
    return allColumns.filter((col: any) => targetVisible.includes(col.id));
  }, [allColumns, visibleColumns, defaultVisibleColumns]);

  const effectivePaginationMode: 'client' | 'server' =
    paginationMode ?? 'client';

  const clientPerPage = perPage ?? 1000;

  const serverControlled =
    effectivePaginationMode === 'server' &&
    serverPagination != null &&
    onServerPaginationChange != null &&
    serverSorting != null &&
    onServerSortingChange != null &&
    serverColumnFilters != null &&
    onServerColumnFiltersChange != null;

  return (
    <BaseHtmlTable
      rows={data}
      columnDefs={columns}
      paginationMode={effectivePaginationMode}
      page={effectivePaginationMode === 'server' ? page : undefined}
      totalPages={effectivePaginationMode === 'server' ? totalPages : undefined}
      total={effectivePaginationMode === 'server' ? total : undefined}
      perPage={effectivePaginationMode === 'server' ? perPage : clientPerPage}
      loading={loading}
      serverPagination={serverControlled ? serverPagination : undefined}
      onServerPaginationChange={serverControlled ? onServerPaginationChange : undefined}
      serverSorting={serverControlled ? serverSorting : undefined}
      onServerSortingChange={serverControlled ? onServerSortingChange : undefined}
      serverColumnFilters={serverControlled ? serverColumnFilters : undefined}
      onServerColumnFiltersChange={serverControlled ? onServerColumnFiltersChange : undefined}
      hiddenColumns={hiddenColumns}
      initialColumnFilters={initialColumnFilters} />
  );
};

export default Table;
